import React, { useEffect, useState, useContext } from 'react';
import Context from '../../../Context';
import { auth, db } from '../../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { apiFetch } from '../../../utils/api';
import { 
  Building2, 
  Wallet, 
  TrendingUp, 
  AlertCircle, 
  RefreshCw, 
  Eye, 
  EyeOff, 
  ChevronDown,
  BarChart3,
  Sparkles,
  Lock,
  Shield,
  Key,
  Link as LinkIcon
} from 'lucide-react';
import styles from './Overview.module.scss';

interface Account {
  account_id: string;
  balances: {
    available: number | null;
    current: number | null;
    limit: number | null;
    iso_currency_code: string | null;
    unofficial_currency_code: string | null;
  };
  mask: string | null;
  name: string;
  official_name: string | null;
  subtype: string | null;
  type: string;
}

const Overview: React.FC = () => {
  const { linkSuccess, linkToken, dispatch } = useContext(Context);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [hideBalances, setHideBalances] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<'all' | 'depository' | 'credit' | 'investment'>('all');
  const [showAccountTypeDropdown, setShowAccountTypeDropdown] = useState(false);
  const [isBankConnected, setIsBankConnected] = useState(false);
  const [showPlaidOverlay, setShowPlaidOverlay] = useState(false);
  const [isVerifyingConnection, setIsVerifyingConnection] = useState(false);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [userName, setUserName] = useState("");

  // Load user preferences and data
  useEffect(() => {
    const loadUserData = async () => {
      if (auth.currentUser) {
        const email = auth.currentUser.email || "";

        try {
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Check bank connection
            if (userData?.plaidAccessToken) {
              // Verify and restore backend connection BEFORE marking as connected
              // to prevent fetchAccounts from firing before the token is in memory.
              try {
                const statusResponse = await apiFetch('/api/connection/status');
                const statusData = await statusResponse.json();
                
                if (!statusData.connected) {
                  // Backend doesn't have token, restore it
                  console.log('🔄 Overview: Restoring backend connection...');
                  const restoreResponse = await apiFetch('/api/connection/restore', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      accessToken: userData.plaidAccessToken,
                      itemId: userData.plaidItemId
                    })
                  });
                  
                  if (restoreResponse.ok) {
                    console.log('✅ Overview: Backend connection restored');
                  } else {
                    console.error('❌ Overview: Failed to restore backend connection');
                  }
                }
              } catch (error) {
                console.error('Error verifying/restoring backend connection:', error);
              }

              // Mark as connected and fetch only after restore is complete
              setIsBankConnected(true);
              fetchAccounts();
            } else {
              setIsBankConnected(false);
              setLoading(false);
            }
            
            // Load hide balances preference
            if (userData.preferences) {
              setHideBalances(userData.preferences.hideBalances || false);
            }
            
            // Get user name
            if (userData?.name) {
              setUserName(userData.name);
            } else if (auth.currentUser.displayName) {
              setUserName(auth.currentUser.displayName);
            } else {
              setUserName(email.split("@")[0]);
            }
          } else {
            setIsBankConnected(false);
            setUserName(auth.currentUser.displayName || email.split("@")[0]);
            setLoading(false);
          }
        } catch (error) {
          console.error("Error loading user data:", error);
          setIsBankConnected(false);
          setUserName(email.split("@")[0]);
          setLoading(false);
        }
      }
    };

    loadUserData();
  }, []);

  // Update when link succeeds
  useEffect(() => {
    if (linkSuccess && isBankConnected) {
      setShowPlaidOverlay(false);
      fetchAccounts();
    }
  }, [linkSuccess, isBankConnected]);

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiFetch('/api/accounts', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        let errorMessage = 'Failed to fetch accounts';
        try {
          const errData = await response.json();
          const code = errData?.error?.error_code;
          if (code === 'NO_ACCOUNTS' || code === 'ITEM_LOGIN_REQUIRED' || code === 'INVALID_ACCESS_TOKEN') {
            errorMessage = 'Your bank connection needs to be refreshed. Please reconnect your bank.';
          } else if (errData?.error?.display_message) {
            errorMessage = errData.error.display_message;
          } else if (typeof errData?.error === 'string') {
            errorMessage = errData.error;
          }
        } catch { /* keep default */ }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setAccounts(data.accounts || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleHideBalances = async () => {
    const newValue = !hideBalances;
    setHideBalances(newValue);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: newValue
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  const handleConnectBank = () => {
    setShowPlaidOverlay(true);
    
    dispatch({
      type: "SET_STATE",
      state: {
        onLinkExit: async () => {
          setShowPlaidOverlay(false);
          
          if (linkSuccess) {
            console.log("🔄 Plaid connection successful - verifying token storage...");
            
            setIsVerifyingConnection(true);
            setVerificationProgress(0);
            setVerificationMessage("Saving your connection securely...");
            
            const maxAttempts = 20;
            let attempts = 0;
            let tokenFound = false;
            
            const pollInterval = setInterval(async () => {
              if (!auth.currentUser) {
                clearInterval(pollInterval);
                setIsVerifyingConnection(false);
                return;
              }

              attempts++;
              setVerificationProgress((attempts / maxAttempts) * 100);

              try {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                
                if (userDoc.exists() && userDoc.data()?.plaidAccessToken) {
                  tokenFound = true;
                  clearInterval(pollInterval);
                  
                  setVerificationProgress(100);
                  setVerificationMessage("Connection verified! ✓");
                  setIsBankConnected(true);
                  
                  setTimeout(() => {
                    setIsVerifyingConnection(false);
                    setVerificationProgress(0);
                    fetchAccounts();
                  }, 1500);
                }
              } catch (error) {
                console.error("Error polling for access token:", error);
              }
              
              if (attempts >= maxAttempts && !tokenFound) {
                clearInterval(pollInterval);
                setVerificationMessage("Connection may have failed. Please try again.");
                
                setTimeout(() => {
                  setIsVerifyingConnection(false);
                  setVerificationProgress(0);
                }, 3000);
              }
            }, 1000);
          }
        }
      }
    });
  };

  const formatCurrency = (amount: number | null | undefined, hide: boolean = false): string => {
    if (hide) return '••••';
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const calculateNetWorth = () => {
    const assets = accounts
      .filter(acc => ['depository', 'investment'].includes(acc.type.toLowerCase()))
      .reduce((sum, acc) => sum + (acc.balances.current || 0), 0);
    
    const liabilities = accounts
      .filter(acc => acc.type.toLowerCase() === 'credit')
      .reduce((sum, acc) => sum + Math.abs(acc.balances.current || 0), 0);
    
    return assets - liabilities;
  };

  const getTotalByType = (type: string) => {
    return accounts
      .filter(account => account.type.toLowerCase() === type.toLowerCase())
      .reduce((sum, account) => sum + (account.balances.current || 0), 0);
  };

  const filteredAccounts = selectedAccountType === 'all' 
    ? accounts 
    : accounts.filter(acc => acc.type.toLowerCase() === selectedAccountType.toLowerCase());

  const getAccountTypeLabel = () => {
    switch (selectedAccountType) {
      case 'all': return 'All Accounts';
      case 'depository': return 'Bank Accounts';
      case 'credit': return 'Credit Cards';
      case 'investment': return 'Investments';
      default: return 'All Accounts';
    }
  };

  const getAccountIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'depository':
        return <Building2 size={20} />;
      case 'credit':
        return <Wallet size={20} />;
      case 'investment':
        return <TrendingUp size={20} />;
      default:
        return <Building2 size={20} />;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading your financial overview...</p>
      </div>
    );
  }

  // Not connected state
  if (!isBankConnected) {
    return (
      <div className={styles.notConnectedContainer}>
        <div className={styles.notConnectedCard}>
          <div className={styles.iconGroup}>
            <Shield size={48} className={styles.primaryIcon} />
            <LinkIcon size={24} className={styles.secondaryIcon} />
          </div>
          <h2>Connect Your Bank Account</h2>
          <p>Get started by securely connecting your bank account to view your financial overview.</p>
          
          <div className={styles.features}>
            <div className={styles.feature}>
              <Lock size={20} />
              <span>Bank-level security</span>
            </div>
            <div className={styles.feature}>
              <Eye size={20} />
              <span>Read-only access</span>
            </div>
            <div className={styles.feature}>
              <Key size={20} />
              <span>Encrypted data</span>
            </div>
          </div>

          <button
            onClick={handleConnectBank}
            className={styles.connectButton}
          >
            <LinkIcon size={20} />
            Connect Bank Account
          </button>

          <p className={styles.securityNote}>
            We use Plaid to securely connect to your bank. Your credentials are never shared with us.
          </p>
        </div>

        {/* Plaid Overlay */}
        {showPlaidOverlay && linkToken && (
          <div className={styles.plaidOverlay}>
            <div className={styles.plaidContent}>
              <h3>Opening Plaid Connection...</h3>
              <div className={styles.spinner}></div>
              <p>A secure window will open to connect your bank account.</p>
            </div>
          </div>
        )}

        {/* Verification Progress Overlay */}
        {isVerifyingConnection && (
          <div className={styles.plaidOverlay}>
            <div className={styles.plaidContent}>
              <h3>{verificationMessage}</h3>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill} 
                  style={{ width: `${verificationProgress}%` }}
                ></div>
              </div>
              <p>{Math.round(verificationProgress)}%</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <AlertCircle size={48} />
        <h3>Unable to Load Accounts</h3>
        <p>{error}</p>
        <button onClick={fetchAccounts} className={styles.retryButton}>
          <RefreshCw size={18} />
          Try Again
        </button>
      </div>
    );
  }

  // Main Overview UI
  return (
    <div className={styles.overview}>
      {/* Header with actions */}
      <div className={styles.headerActions}>
        <button 
          onClick={toggleHideBalances} 
          className={styles.toggleBalanceBtn}
          title={hideBalances ? "Show balances" : "Hide balances"}
        >
          {hideBalances ? <Eye size={18} /> : <EyeOff size={18} />}
          {hideBalances ? "Show" : "Hide"}
        </button>
        <button 
          onClick={fetchAccounts} 
          className={styles.refreshButton}
          title="Refresh data"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Welcome message */}
      <div className={styles.welcomeSection}>
        <h2>Welcome back, {userName} !</h2>
        {lastUpdated && (
          <p className={styles.lastUpdated}>
            Last updated: {lastUpdated.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </p>
        )}
      </div>

      {/* Net Worth Card */}
      <div className={styles.netWorthCard}>
        <div className={styles.netWorthHeader}>
          <Sparkles size={24} className={styles.sparkleIcon} />
          <h3>Net Worth</h3>
        </div>
        <div className={styles.netWorthAmount}>
          {formatCurrency(calculateNetWorth(), hideBalances)}
        </div>
        <div className={styles.netWorthBreakdown}>
          <div className={styles.breakdownItem}>
            <span className={styles.breakdownLabel}>
              <TrendingUp size={16} />
              Assets
            </span>
            <span className={styles.breakdownValue}>
              {formatCurrency(
                accounts
                  .filter(acc => ['depository', 'investment'].includes(acc.type.toLowerCase()))
                  .reduce((sum, acc) => sum + (acc.balances.current || 0), 0),
                hideBalances
              )}
            </span>
          </div>
          <div className={styles.breakdownItem}>
            <span className={styles.breakdownLabel}>
              <Wallet size={16} />
              Liabilities
            </span>
            <span className={styles.breakdownValue}>
              {formatCurrency(
                accounts
                  .filter(acc => acc.type.toLowerCase() === 'credit')
                  .reduce((sum, acc) => sum + Math.abs(acc.balances.current || 0), 0),
                hideBalances
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Account Type Summary */}
      <div className={styles.accountTypeSummary}>
        <div className={styles.summaryCard}>
          <Building2 size={24} className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <span className={styles.summaryLabel}>Bank Accounts</span>
            <span className={styles.summaryAmount}>
              {formatCurrency(getTotalByType('depository'), hideBalances)}
            </span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <Wallet size={24} className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <span className={styles.summaryLabel}>Credit Cards</span>
            <span className={styles.summaryAmount}>
              {formatCurrency(Math.abs(getTotalByType('credit')), hideBalances)}
            </span>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <TrendingUp size={24} className={styles.summaryIcon} />
          <div className={styles.summaryContent}>
            <span className={styles.summaryLabel}>Investments</span>
            <span className={styles.summaryAmount}>
              {formatCurrency(getTotalByType('investment'), hideBalances)}
            </span>
          </div>
        </div>
      </div>

      {/* Account Type Filter */}
      <div className={styles.filterSection}>
        <h3>Accounts</h3>
        <div className={styles.accountTypeSelector}>
          <button
            className={styles.accountTypeTrigger}
            onClick={() => setShowAccountTypeDropdown(!showAccountTypeDropdown)}
          >
            {getAccountTypeLabel()}
            <ChevronDown size={16} />
          </button>
          
          {showAccountTypeDropdown && (
            <>
              <div 
                className={styles.dropdownBackdrop} 
                onClick={() => setShowAccountTypeDropdown(false)}
              />
              <div className={styles.accountTypeDropdown}>
                <button
                  className={selectedAccountType === 'all' ? styles.active : ''}
                  onClick={() => {
                    setSelectedAccountType('all');
                    setShowAccountTypeDropdown(false);
                  }}
                >
                  <BarChart3 size={16} />
                  All Accounts
                </button>
                <button
                  className={selectedAccountType === 'depository' ? styles.active : ''}
                  onClick={() => {
                    setSelectedAccountType('depository');
                    setShowAccountTypeDropdown(false);
                  }}
                >
                  <Building2 size={16} />
                  Bank Accounts
                </button>
                <button
                  className={selectedAccountType === 'credit' ? styles.active : ''}
                  onClick={() => {
                    setSelectedAccountType('credit');
                    setShowAccountTypeDropdown(false);
                  }}
                >
                  <Wallet size={16} />
                  Credit Cards
                </button>
                <button
                  className={selectedAccountType === 'investment' ? styles.active : ''}
                  onClick={() => {
                    setSelectedAccountType('investment');
                    setShowAccountTypeDropdown(false);
                  }}
                >
                  <TrendingUp size={16} />
                  Investments
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Accounts List */}
      <div className={styles.accountsList}>
        {filteredAccounts.length === 0 ? (
          <div className={styles.noAccountsMessage}>
            <AlertCircle size={32} />
            <p>No {selectedAccountType !== 'all' && getAccountTypeLabel().toLowerCase()} found</p>
          </div>
        ) : (
          <div className={styles.accountsGrid}>
            {filteredAccounts.map((account) => (
              <div key={account.account_id} className={styles.accountCard}>
                <div className={styles.accountHeader}>
                  <div className={styles.accountIconWrapper}>
                    {getAccountIcon(account.type)}
                  </div>
                  <div className={styles.accountInfo}>
                    <h4>{account.name}</h4>
                    {account.mask && (
                      <span className={styles.accountMask}>•••• {account.mask}</span>
                    )}
                  </div>
                </div>

                <div className={styles.accountBalance}>
                  <span className={styles.balanceLabel}>Current Balance</span>
                  <span className={styles.balanceAmount}>
                    {formatCurrency(account.balances.current, hideBalances)}
                  </span>
                </div>

                {account.balances.available !== null && 
                 account.balances.available !== account.balances.current && (
                  <div className={styles.accountDetail}>
                    <span>Available:</span>
                    <span>{formatCurrency(account.balances.available, hideBalances)}</span>
                  </div>
                )}

                {account.type.toLowerCase() === 'credit' && account.balances.limit && (
                  <div className={styles.accountDetail}>
                    <span>Credit Limit:</span>
                    <span>{formatCurrency(account.balances.limit, hideBalances)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
